import Component from '@glimmer/component'
import { action } from '@ember/object'
import { service } from '@ember/service'
import { task, all, timeout, didCancel, type TaskInstance } from 'ember-concurrency'
import { t, type IntlService } from 'ember-intl'
import type FlashQueueService from 'teacher-ui-client/services/flash-queue'
import type { Log } from '@blakeelearning/log'
import type SchoolClass from 'teacher-ui-client/models/school-class'
import { tracked } from '@glimmer/tracking'
import { LayoutBlocksConfirmModal } from 'teacher-ui-client/components/layout-blocks/confirm-modal'
import PhosphorIcon from 'teacher-ui-client/components/phosphor-icon'
import { get } from '@ember/helper'
import { or } from 'ember-truth-helpers'

interface Args {
  classesToDelete: SchoolClass[]
  afterAction: (options?: { refreshClasses?: boolean }) => void
}

interface Signature {
  Args: Args
}

/**
 * A modal that shows a list of classes for deletion and some data about them. On confirmation it deletes the classes
 * one by one and displays the deletion status of each.
 */
export class RolloverDeleteClassesPrompt extends Component<Signature> {
  @service intl!: IntlService
  @service flashQueue!: FlashQueueService
  @service log!: Log

  @tracked deletions: Record<string, TaskInstance<Promise<unknown>>> = {}

  deleteClasses = task({ drop: true }, async (classesToDelete: SchoolClass[]) => {
    try {
      // run a delete task for each, paired with id, so the UI can view the state of each deletion task by class id
      this.deletions = classesToDelete.reduce((acc, schoolClass) => {
        return schoolClass.id ? { ...acc, [schoolClass.id]: this._deleteClass.perform(schoolClass) } : acc
      }, {})

      // disable this eslint rule, as we are doing what it says (instead of Promise.all we're using e-c all() function)
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      await all(Object.values(this.deletions))
      this.flashQueue.addSuccess({ subtitle: this.intl.t('manage.rollover.steps.delete_classes.deletePrompt.success') })
    } catch (e) {
      if (!didCancel(e)) {
        this.flashQueue.addFail({ subtitle: this.intl.t('manage.rollover.steps.delete_classes.deletePrompt.failed') })
        if (e instanceof Error) {
          this.log.error('failed to delete class during rollover', e)
        }
      }
    } finally {
      // just to let the finished / cancelled / errored UX display for a sec before (likely) closing the modal
      await timeout(1000)
      /* Wait for any in flight delete requests to settle (in case of cancel). If the class is huge the deletion request
       * can take a while to return, and we don't want them trying a new deletion while that one's happening in the bg
       */
      while (classesToDelete.some((schoolClass: SchoolClass) => schoolClass.isSaving)) {
        await timeout(1000)
      }
      this.deletions = {}
      this.args.afterAction({ refreshClasses: true })
    }
  })

  // queue up, execute one at a time, in order. Don't want to overload gravity.
  _deleteClass = task({ enqueue: true }, async (schoolClass: SchoolClass) => {
    /**
     * Space the deletions apart slightly, so that if the prior deletion errored, there's time to cancel this task
     * before it begins deleting the next record
     */
    await timeout(100)

    await schoolClass.destroyRecord()
  })

  @action
  confirmDelete() {
    if (this.args.classesToDelete) void this.deleteClasses.perform(this.args.classesToDelete)
  }

  @action
  cancel() {
    if (this.deleteClasses.isRunning) {
      void this.deleteClasses.cancelAll()
    } else {
      // user hit cancel before deleting any classes
      this.args.afterAction()
    }
  }

  get classesToDeleteExists(): boolean {
    return this.args.classesToDelete?.length > 0
  }

  <template>
    <LayoutBlocksConfirmModal
      @title={{t "manage.rollover.steps.delete_classes.deletePrompt.title"}}
      @showModal={{this.classesToDeleteExists}}
      @cancel={{this.cancel}}
      @cancelButtonText={{t "cancel"}}
      @confirm={{this.confirmDelete}}
      @confirmButtonText={{t "manage.rollover.steps.delete_classes.deletePrompt.submit"}}
      @isSubmitting={{this.deleteClasses.isRunning}}
      @allowCancelDuringSubmission={{true}}
    >
      <div class="m-6 space-y-6 text-xl" data-test-rollover-delete-classes-prompt>
        <p>
          {{t "manage.rollover.steps.delete_classes.deletePrompt.aboutToDelete" htmlSafe=true}}
          <ul class="max-h-1/2-screen overflow-y-auto" data-test-delete-class-list>

            {{#each @classesToDelete as |schoolClass|}}
              {{#if schoolClass.id}}
                {{#let (get this.deletions schoolClass.id) as |deletionInstance|}}
                  <li class="overflow-hidden" data-test-delete-class-item={{schoolClass.id}}>
                    {{#if deletionInstance.isRunning}}
                      <PhosphorIcon @icon="phosphor-circle-notch-bold" class="animate-spin" />
                    {{else if deletionInstance.isSuccessful}}
                      <PhosphorIcon @icon="phosphor-check-bold" class="text-forest-green-350" data-test-success-icon />
                    {{else if (or deletionInstance.isError deletionInstance.isCanceled)}}
                      <PhosphorIcon @icon="phosphor-x-bold" class="text-watermelony-red-350" />
                    {{else}}
                      -
                    {{/if}}
                    {{t
                      "manage.rollover.steps.delete_classes.deletePrompt.listItem"
                      htmlSafe=true
                      className=schoolClass.name
                      teacherCount=schoolClass.teachers.length
                      studentCount=schoolClass.students.length
                    }}
                  </li>
                {{/let}}
              {{/if}}
            {{/each}}
          </ul>
        </p>
        <p>{{t "manage.rollover.steps.delete_classes.deletePrompt.areYouSure"}}</p>
        <p>{{t "manage.rollover.steps.delete_classes.deletePrompt.thisAction" htmlSafe=true}}</p>
      </div>
    </LayoutBlocksConfirmModal>
  </template>
}

export default RolloverDeleteClassesPrompt
